import React, { useState, useEffect } from 'react';
import { DEPARTMENTS, fetchWithFallback } from '../referenceData';

export default function Wash({ api, site }) {
  const [plans, setPlans] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [date, setDate] = useState((() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })());
  const [shiftType, setShiftType] = useState('day');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [fclmData, setFclmData] = useState(null);
  const [volumeBridge, setVolumeBridge] = useState('');
  const [hoursBridge, setHoursBridge] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 11 });

  useEffect(() => {
    fetchWithFallback(`${api}/api/shift-plans`, []).then(setPlans);
    fetchWithFallback(`${api}/api/departments`, DEPARTMENTS).then(setDepartments);
  }, [api]);

  // Auto-select plan when date/shift changes
  useEffect(() => {
    const match = plans.find(p => p.date === date && p.shift_type === shiftType);
    if (match) setSelectedPlan(String(match.id));
    else { setSelectedPlan(''); }
  }, [date, shiftType, plans]);

  useEffect(() => {
    if (selectedPlan) {
      setLoading(true);
      setProgress({ current: 0, total: 11 });
      const pollProgress = setInterval(() => {
        fetch(`${api}/api/fclm/progress`).then(r => r.json()).then(setProgress).catch(() => {});
      }, 2000);
      fetch(`${api}/api/fclm/hourly?site=${site}&date=${date}&shift_type=${shiftType}`)
        .then(r => r.json()).then(d => { setFclmData(d); setLoading(false); clearInterval(pollProgress); })
        .catch(() => { setLoading(false); clearInterval(pollProgress); });
    }
  }, [selectedPlan, api, site, date, shiftType]);

  const plan = plans.find(p => p.date === date && p.shift_type === shiftType);
  const pidGoal = plan?.volumes?.find(v => v.key === 'pid')?.volume || 0;
  const arosGoal = plan?.volumes?.find(v => v.key === 'aros')?.volume || 0;
  const prGoal = plan?.volumes?.find(v => v.key === 'pallet')?.volume || 0;
  const ibGoal = pidGoal + arosGoal + prGoal;
  const sorterGoal = Math.round(pidGoal * 0.75);
  const fluidGoal = Math.round(pidGoal * 0.75) + arosGoal;
  const pitGoal = prGoal;
  const obGoal = ibGoal;

  const actual = fclmData?.cartons || {};
  const ibActual = actual.inbound?.total || 0;
  const obActual = actual.outbound?.total || 0;
  const sorterActual = actual.pid?.total || 0;
  const arosActual = actual.aros?.total || 0;
  const prActual = actual.pr?.total || 0;
  const fluidActual = (actual.fluidDirect?.total || 0) + (actual.fluidOffline?.total || 0);
  const pitActual = actual.pit?.total || 0;
  const pidActual = actual.pid?.total || 0;

  // PPR hours
  const pprFcSummary = fclmData?.ppr_hours?.fcSummary ? Object.values(fclmData.ppr_hours.fcSummary).reduce((s, v) => s + v, 0) : 0;
  const pprInbound = fclmData?.ppr_hours?.inbound ? Object.values(fclmData.ppr_hours.inbound).reduce((s, v) => s + v, 0) : 0;
  const pprTransferOut = fclmData?.ppr_hours?.transferOut ? Object.values(fclmData.ppr_hours.transferOut).reduce((s, v) => s + v, 0) : 0;

  // Shift plan hours
  const spHours = plan?.group_hours?.reduce((s, g) => s + g.hours, 0) || 0;
  const spIBHours = plan?.group_hours?.find(g => g.group_id === 1)?.hours || 0;
  const spOBHours = plan?.group_hours?.find(g => g.group_id === 2)?.hours || 0;

  // Goal (OP) rates from LP
  const dilutedLP = 48, ibLP = 63, obLP = 44;
  const opTotalHrs = ibGoal > 0 ? (ibGoal + obGoal) / dilutedLP : 0;
  const opIBHrs = ibGoal > 0 ? ibGoal / ibLP : 0;
  const opOBHrs = obGoal > 0 ? obGoal / obLP : 0;
  const opDirectHrs = opIBHrs + opOBHrs;

  // Actuals
  const actualDiluted = pprFcSummary > 0 ? (ibActual + obActual) / pprFcSummary : 0;
  const actualIBCPLH = pprInbound > 0 ? ibActual / pprInbound : 0;
  const actualOBCPLH = pprTransferOut > 0 ? obActual / pprTransferOut : 0;
  const actualDirectCPLH = (pprInbound + pprTransferOut) > 0 ? (ibActual + obActual) / (pprInbound + pprTransferOut) : 0;

  const spDiluted = spHours > 0 ? (ibGoal + obGoal) / spHours : 0;
  const spDirectCPLH = (spIBHours + spOBHours) > 0 ? (ibGoal + obGoal) / (spIBHours + spOBHours) : 0;
  const spIBCPLH = spIBHours > 0 ? ibGoal / spIBHours : 0;
  const spOBCPLH = spOBHours > 0 ? obGoal / spOBHours : 0;

  const pctColor = (pct) => pct >= 100 ? '#e8f5e9' : pct >= 95 ? '#fff3e0' : '#ffebee';
  const deltaColor = (d) => d >= 0 ? '#2e7d32' : '#c62828';

  const volRows = [
    { metric: 'Total IB Cartons', goal: ibGoal, actual: ibActual },
    { metric: 'Total OB Cartons', goal: obGoal, actual: obActual },
    { metric: 'Sorter Cartons', goal: sorterGoal, actual: sorterActual },
    { metric: 'AROS Cartons', goal: arosGoal, actual: arosActual },
    { metric: 'MR Cartons', goal: 0, actual: 0 },
    { metric: 'PR Cartons', goal: prGoal, actual: prActual },
    { metric: 'PR Pallets', goal: Math.round(prGoal / 27.5), actual: 0 },
    { metric: 'Fluid Cartons', goal: fluidGoal, actual: fluidActual },
    { metric: 'PIT Cartons', goal: pitGoal, actual: pitActual },
    { metric: 'Each Receive Cartons', goal: null, actual: 0 },
    { metric: 'Problem Solve Cartons', goal: null, actual: actual.problemSolve?.total || 0 },
    { metric: 'Prep Receive Cartons', goal: null, actual: 0 },
  ];

  const rateRows = [
    { metric: 'All In (Diluted)', sp: spDiluted, op: dilutedLP, actual: actualDiluted },
    { metric: 'Direct Throughput (CPLH)', sp: spDirectCPLH, op: (ibLP + obLP) / 2, actual: actualDirectCPLH },
    { metric: 'IB CPLH', sp: spIBCPLH, op: ibLP, actual: actualIBCPLH },
    { metric: 'OB CPLH', sp: spOBCPLH, op: obLP, actual: actualOBCPLH },
  ];

  const hoursRows = [
    { metric: 'Total Throughput', sp: spHours, op: opTotalHrs, actual: pprFcSummary },
    { metric: 'Direct Hours', sp: spIBHours + spOBHours, op: opDirectHrs, actual: pprInbound + pprTransferOut },
    { metric: 'IB Hours', sp: spIBHours, op: opIBHrs, actual: pprInbound },
    { metric: 'OB Hours', sp: spOBHours, op: opOBHrs, actual: pprTransferOut },
    { metric: 'Support Hours', sp: 'NA', op: null, actual: pprFcSummary - pprInbound - pprTransferOut },
    { metric: 'FCNC/Overstaff Hours', sp: null, op: null, actual: 0 },
  ];

  const cellStyle = { padding: '6px 10px', textAlign: 'right', fontSize: '0.82rem' };
  const headerCell = { ...cellStyle, fontWeight: 600, background: '#f5f5f5' };

  return (
    <div>
      <h2 className="section-title">WASH — {site}</h2>
      <div className="card">
        <div className="form-row">
          <div className="form-group">
            <label>Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Shift Type</label>
            <select value={shiftType} onChange={e => setShiftType(e.target.value)}>
              <option value="day">Day</option>
              <option value="night">Night</option>
            </select>
          </div>
        </div>
      </div>

      {loading && (
        <div className="card">
          <p style={{ fontSize: '0.85rem', marginBottom: 8 }}>Pulling FCLM data... Hour {progress.current} of {progress.total}</p>
          <div style={{ background: '#e0e0e0', borderRadius: 4, height: 20, overflow: 'hidden' }}>
            <div style={{ background: '#232f3e', height: '100%', width: `${(progress.current / progress.total) * 100}%`, transition: 'width 0.5s', borderRadius: 4 }}></div>
          </div>
        </div>
      )}

      {selectedPlan && (
        <>
          {/* Dept Wash Status */}
          <DeptWashStatus api={api} site={site} date={date} shiftType={shiftType} departments={departments} />

          <div className="card">
            <h3 className="section-title">Volume Execution</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', marginBottom: 16 }}>
              <thead>
                <tr style={{ background: '#e8eaf6' }}>
                  <th style={{ ...headerCell, textAlign: 'left' }}>Metric</th>
                  <th style={headerCell}>BB Goal</th>
                  <th style={headerCell}>Actual</th>
                  <th style={headerCell}>Delta</th>
                </tr>
              </thead>
              <tbody>
                {volRows.map(r => {
                  const delta = r.goal !== null ? r.actual - r.goal : null;
                  return (
                    <tr key={r.metric} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 500 }}>{r.metric}</td>
                      <td style={cellStyle}>{r.goal !== null ? r.goal.toLocaleString() : ''}</td>
                      <td style={cellStyle}>{r.actual ? r.actual.toLocaleString() : '0'}</td>
                      <td style={{ ...cellStyle, color: delta !== null ? deltaColor(delta) : '#666' }}>{delta !== null ? (delta >= 0 ? '+' : '') + delta.toLocaleString() : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>Volume Bridge</label>
            <textarea value={volumeBridge} onChange={e => setVolumeBridge(e.target.value)}
              style={{ width: '100%', height: 100, padding: 10, border: '1px solid #ddd', borderRadius: 4, fontSize: '0.85rem', resize: 'vertical', marginTop: 4 }}
              placeholder="Enter volume bridge explanation..." />
          </div>

          <div className="card">
            <h3 className="section-title">Rate Execution</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ background: '#e8eaf6' }}>
                  <th style={{ ...headerCell, textAlign: 'left' }}>Metric</th>
                  <th style={headerCell}>Shift Plan</th>
                  <th style={headerCell}>Goal (OP)</th>
                  <th style={headerCell}>Actual</th>
                  <th style={headerCell}>% to SP</th>
                  <th style={headerCell}>% to OP</th>
                </tr>
              </thead>
              <tbody>
                {rateRows.map(r => {
                  const pctSP = r.sp > 0 ? Math.round((r.actual / r.sp) * 100) : 0;
                  const pctOP = r.op > 0 ? Math.round((r.actual / r.op) * 100) : 0;
                  return (
                    <tr key={r.metric} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 500 }}>{r.metric}</td>
                      <td style={cellStyle}>{r.sp ? r.sp.toFixed(1) : '—'}</td>
                      <td style={cellStyle}>{r.op ? r.op.toFixed(1) : '—'}</td>
                      <td style={cellStyle}>{r.actual ? r.actual.toFixed(1) : '—'}</td>
                      <td style={{ ...cellStyle, background: pctColor(pctSP) }}>{pctSP}%</td>
                      <td style={{ ...cellStyle, background: pctColor(pctOP) }}>{pctOP}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', marginTop: 16 }}>
              <thead>
                <tr style={{ background: '#e8eaf6' }}>
                  <th style={{ ...headerCell, textAlign: 'left' }}>Metric</th>
                  <th style={headerCell}>SP Hours</th>
                  <th style={headerCell}>OP Hours</th>
                  <th style={headerCell}>Actual</th>
                  <th style={headerCell}>SP Δ</th>
                  <th style={headerCell}>OP Δ</th>
                </tr>
              </thead>
              <tbody>
                {hoursRows.map(r => {
                  const spDelta = r.sp && r.sp !== 'NA' ? r.actual - r.sp : null;
                  const opDelta = r.op ? r.actual - r.op : null;
                  return (
                    <tr key={r.metric} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 500 }}>{r.metric}</td>
                      <td style={cellStyle}>{r.sp === 'NA' ? 'NA' : r.sp ? r.sp.toFixed(0) : '—'}</td>
                      <td style={cellStyle}>{r.op ? r.op.toFixed(0) : ''}</td>
                      <td style={cellStyle}>{r.actual ? r.actual.toFixed(1) : '0.0'}</td>
                      <td style={{ ...cellStyle, color: spDelta !== null ? deltaColor(spDelta) : '#666' }}>{spDelta !== null ? (spDelta >= 0 ? '+' : '') + spDelta.toFixed(1) : ''}</td>
                      <td style={{ ...cellStyle, color: opDelta !== null ? deltaColor(opDelta) : '#666' }}>{opDelta !== null ? (opDelta >= 0 ? '+' : '') + opDelta.toFixed(1) : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ marginTop: 16 }}>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>Hours Bridge</label>
              <textarea value={hoursBridge} onChange={e => setHoursBridge(e.target.value)}
                style={{ width: '100%', height: 100, padding: 10, border: '1px solid #ddd', borderRadius: 4, fontSize: '0.85rem', resize: 'vertical', marginTop: 4 }}
                placeholder="Enter hours/cost bridge explanation..." />
            </div>
          </div>

          <div className="card" style={{ textAlign: 'center' }}>
            <button className="btn btn-primary" style={{ padding: '12px 40px', fontSize: '1rem' }} onClick={async () => {
              if (!fclmData || !plan) return;
              const res = await fetch(`${api}/api/shift-actuals`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  shift_plan_id: plan.id,
                  site,
                  date: plan.date,
                  shift_type: plan.shift_type,
                  cartons: fclmData.cartons,
                  ppr_hours: fclmData.ppr_hours,
                  labor: fclmData.labor,
                  bridges: { volume: volumeBridge, hours: hoursBridge }
                })
              });
              const data = await res.json();
              if (data.success) alert(`Shift submitted! Corner: ${data.corner}`);
            }}>Submit Shift</button>
            <p style={{ fontSize: '0.8rem', color: '#666', marginTop: 8 }}>Saves all actuals for historic tracking in Heapmat</p>
          </div>
        </>
      )}
    </div>
  );
}

function DeptWashStatus({ api, site, date, shiftType, departments }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (date && shiftType) {
      fetch(`${api}/api/dept-wash/status?site=${site}&date=${date}&shift_type=${shiftType}`)
        .then(r => r.json()).then(setStatus).catch(() => setStatus(null));
    }
  }, [api, site, date, shiftType]);

  const finalize = async () => {
    await fetch(`${api}/api/dept-wash/finalize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ site, date, shift_type: shiftType })
    });
    setStatus(prev => ({ ...prev, finalized: true }));
  };

  if (!status) return null;

  return (
    <div className="card">
      <h3 className="section-title">Dept Wash Status</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        {departments.map(d => {
          const submitted = status.submitted.includes(d.id);
          return (
            <span key={d.id} style={{ padding: '4px 10px', borderRadius: 4, fontSize: '0.8rem', fontWeight: 500,
              background: submitted ? '#c8e6c9' : '#ffcdd2', color: submitted ? '#2e7d32' : '#c62828' }}>
              {submitted ? '✓' : '✗'} {d.name}
            </span>
          );
        })}
      </div>
      {status.finalized ? (
        <p style={{ color: '#2e7d32', fontWeight: 600 }}>✓ Shift wash finalized</p>
      ) : status.missing.length === 0 ? (
        <button className="btn btn-primary" onClick={finalize}>Finalize Shift Wash</button>
      ) : (
        <p style={{ fontSize: '0.8rem', color: '#666' }}>Waiting on {status.missing.length} dept(s) before finalize</p>
      )}
    </div>
  );
}
